<template>
  <page-template :is-loading="isLoading" :data-exists="selectedSecurityPolicyData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('securityPolicy.configPage.newPageHeadline')
          : $t('securityPolicy.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft v-if="!readOnly">
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="Security Policies List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedSecurityPolicy && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          v-if="selectedSecurityPolicy"
          :items="dropdownData.map((item) => [item.id, item.name] as [string, string])"
          :disabled="isLoading"
          v-model:value="selectedSecurityPolicy"
          @update:value="switchSecurityPolicy($event as string)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('securityPolicy.configPage.switchTooltip')"
          classes="switch-security-policy switch-page-entry"
          data-qa="switch-security-policy"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight v-if="!readOnly">
      <template v-if="selectedSecurityPolicy && !isNew">
        <rbz-form-button
          :label="$t('common.operations.duplicate.label')"
          variant="tertiary"
          icon="IconDuplicate"
          @button-click="duplicateSecurityPolicy()"
          class="duplicate-button"
          :disabled="isLoading || entityNotSaved"
          :tooltip="
            entityNotSaved ? $t('common.operations.duplicate.disabledTooltip.notSaved') : ''
          "
          data-qa="duplicate-button"
        />
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadSecurityPolicy()"
          class="download-button"
          :disabled="isLoading"
          data-qa="download-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteSecurityPolicyModal()"
          class="delete-button"
          :disabled="
            isLoading || selectedSecurityPolicyProtected || selectedSecurityPolicyReferenced
          "
          :tooltip="deleteTooltip"
          data-qa="delete-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isSecurityPolicyValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-columns">
        <div class="rbz-column is-4">
          <div class="rbz-field">
            <rbz-form-input
              name="name"
              classes="name"
              data-qa="name-input"
              :label="$t('securityPolicy.configPage.name')"
              :placeholder="$t('securityPolicy.configPage.name')"
              v-model:inputValue="selectedSecurityPolicyData.name"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <rbz-form-textarea
              name="description"
              classes="description"
              data-qa="description-textarea"
              :label="$t('securityPolicy.configPage.description')"
              v-model:inputValue="selectedSecurityPolicyData.description"
              rows="2"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <tags-selection
              :value="selectedSecurityPolicyData.tags"
              @update:value="selectedSecurityPolicyData.tags = $event as string[]"
              :label="$t('securityPolicy.configPage.tags')"
              :disabled="readOnly"
            />
            <div class="automatic-tags">
              <template v-for="tag in automaticTags" :key="tag">
                <rbz-custom-tag :value="tag" :disabled="readOnly" />
              </template>
            </div>
          </div>
        </div>
        <div class="rbz-column is-8">
          <div class="rbz-field" :class="{ 'is-active': mainSessionIdFieldActive }">
            <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
              {{ $t('securityPolicy.configPage.mainSessionId') }}
            </label>
            <key-option
              selected-type-column-class="is-3"
              v-model:option="selectedSecurityPolicyData.session[0]"
              :ignore-attributes="['session']"
              @focusin="mainSessionIdFieldActive = true"
              @focusout="mainSessionIdFieldActive = false"
              :disabled="readOnly"
            />
          </div>
          <div class="rbz-field">
            <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
              {{ $t('securityPolicy.configPage.otherSessionIds') }}
            </label>
            <key-option
              v-for="(keyOption, index) in sessionIdsKeyOptions"
              :key="keyOption.id"
              show-remove
              @remove="removeSessionId(index)"
              v-model:option="sessionIdsKeyOptions[index].value"
              selected-type-column-class="is-3"
              removable
              :ignore-attributes="['session']"
              :disabled="readOnly"
            />
            <div
              v-if="!isSecurityPolicyValid && sessionErrorMessage !== ''"
              class="rbz-text-medium rbz-text-danger mb-2"
            >
              {{ $t(sessionErrorMessage) }}
            </div>
            <rbz-form-button
              :label="$t('securityPolicy.configPage.newSessionId')"
              variant="secondary"
              classes="new-session-id-button"
              data-qa="add-session-id-button"
              @button-click="addSessionId()"
              :disabled="readOnly"
            />
          </div>
        </div>
      </div>
      <div class="rbz-field">
        <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
          {{ $t('securityPolicy.configPage.pathMapping.title') }}
        </label>
        <rbz-table
          :columns="pathMappingColumns"
          :row-classes="
            (item) => {
              const itemPathValid = isSelectedMapEntryPathValid(item.id);
              return !itemPathValid ? 'has-errors' : '';
            }
          "
          :table-data="selectedSecurityPolicyData.map"
          rows-per-page="20"
          ref="pathMappingTable"
          class="path-mapping-table"
          data-qa="path-mapping-table"
          :show-buttons-column="!readOnly"
          :row-buttons="pathMappingRowButtons"
          :show-top-navbar="false"
          :show-filter-row="true"
          :show-expander-column="true"
          :persist-expander-data="true"
          @delete-row-button-clicked="removeMapEntry"
        >
          <template #rowExpander="rowProps">
            <div class="rbz-columns">
              <div
                class="rbz-column"
                :class="{ 'is-8': !isSiteLevelEntry(rowProps.row as SecurityPolicyEntryMatch) }"
              >
                <div
                  v-if="!isSiteLevelEntry(rowProps.row as SecurityPolicyEntryMatch)"
                  class="rbz-field"
                >
                  <rbz-form-input
                    name="map-entry-name-input"
                    data-qa="map-entry-name-input"
                    classes="map-entry-name-input"
                    :label="$t('securityPolicy.configPage.pathMapping.name')"
                    :placeholder="$t('securityPolicy.configPage.pathMapping.name')"
                    :disabled="
                      isMapEntryProtected(rowProps.row as SecurityPolicyEntryMatch) || readOnly
                    "
                    v-model:inputValue="rowProps.row.name"
                  />
                </div>
                <div
                  v-if="!isSiteLevelEntry(rowProps.row as SecurityPolicyEntryMatch)"
                  class="rbz-field"
                >
                  <rbz-form-input
                    name="map-entry-match-input"
                    data-qa="map-entry-match-input"
                    classes="map-entry-match-input"
                    :label="$t('securityPolicy.configPage.pathMapping.matchPathField')"
                    :placeholder="$t('securityPolicy.configPage.pathMapping.matchPathPlaceholder')"
                    icon="IconCode"
                    :is-invalid="!isSelectedMapEntryPathValid(rowProps.row.id)"
                    :error-message-text="mapEntryPathErrorMessage(rowProps.row.id)"
                    :disabled="
                      isMapEntryProtected(rowProps.row as SecurityPolicyEntryMatch) || readOnly
                    "
                    :ref="`mapEntryPath${rowProps.row.id}`"
                    v-model:inputValue="rowProps.row.match"
                  />
                </div>
                <div class="rbz-field">
                  <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
                    {{ $t('securityPolicy.configPage.pathMapping.rateLimitRules') }}
                  </label>
                  <rbz-table
                    :columns="rateLimitRulesColumns"
                    :table-data="
                      rowProps.row['rate_limit_rules'].map((item: string) => {
                        return {
                          id: item,
                        };
                      })
                    "
                    rows-per-page="20"
                    class="rate-limit-rules-table"
                    data-qa="rate-limit-rules-table"
                    :show-buttons-column="!readOnly"
                    :row-buttons="rateLimitRulesRowButtons"
                    :show-download-button="false"
                    :show-filter-button="false"
                    :show-new-as-row="true"
                    :show-new-button="!readOnly"
                    :sticky-header="false"
                    @add-inline-data="
                      addRateLimitRuleToEntry(rowProps.row as SecurityPolicyEntryMatch, $event.id)
                    "
                    @delete-row-button-clicked="
                      removeRateLimitRuleFromEntry(
                        rowProps.row as SecurityPolicyEntryMatch,
                        $event.id
                      )
                    "
                  >
                    <template #newRowOverride="newRowProps">
                      <td colspan="3">
                        <rbz-dropdown
                          classes="rate-limit-rule-selection"
                          :items="
                            newRateLimitRule(rowProps.row as SecurityPolicyEntryMatch).map(
                              (item) => {
                                return [item.id, item.name] as [string, string];
                              }
                            )
                          "
                          data-qa="rate-limit-rule-dropdown"
                          v-model:value="newRowProps.data.id"
                        />
                      </td>
                    </template>
                  </rbz-table>
                </div>
                <div class="rbz-field">
                  <label class="rbz-label" :class="{ disabled: planetStore.isUpdateInProcess }">
                    {{ $t('securityPolicy.configPage.pathMapping.edgeFunctions') }}
                  </label>
                  <rbz-table
                    :columns="edgeFunctionsColumns"
                    :table-data="
                      rowProps.row['edge_functions'].map((item: string) => {
                        return {
                          id: item,
                        };
                      })
                    "
                    rows-per-page="20"
                    class="edge-functions-table"
                    data-qa="edge-functions-table"
                    :show-buttons-column="!readOnly"
                    :row-buttons="edgeFunctionsRowButtons"
                    :show-download-button="false"
                    :show-filter-button="false"
                    :show-new-as-row="true"
                    :show-new-button="!readOnly"
                    :sticky-header="false"
                    @add-inline-data="
                      addEdgeFunctionToEntry(rowProps.row as SecurityPolicyEntryMatch, $event.id)
                    "
                    @delete-row-button-clicked="
                      removeEdgeFunctionFromEntry(
                        rowProps.row as SecurityPolicyEntryMatch,
                        $event.id
                      )
                    "
                  >
                    <template #newRowOverride="newRowProps">
                      <td colspan="3">
                        <rbz-dropdown
                          classes="edge-function-selection"
                          :items="
                            newEdgeFunctions(rowProps.row as SecurityPolicyEntryMatch).map(
                              (item) => {
                                return [item.id, item.name] as [string, string];
                              }
                            )
                          "
                          data-qa="edge-function-dropdown"
                          v-model:value="newRowProps.data.id"
                        />
                      </td>
                    </template>
                  </rbz-table>
                </div>
              </div>
              <div
                v-if="!isSiteLevelEntry(rowProps.row as SecurityPolicyEntryMatch)"
                class="rbz-column is-4 right-side-column"
              >
                <div class="top-section">
                  <div class="rbz-field">
                    <rbz-dropdown
                      :items="
                        contentFilterProfiles.map(
                          (item) => [item.id, item.name] as [string, string]
                        )
                      "
                      v-model:value="rowProps.row['content_filter_profile']"
                      :label="$t('securityPolicy.configPage.pathMapping.contentFilterProfile')"
                      data-qa="content-filter-profile-dropdown"
                      :disabled="readOnly"
                    />
                    <rbz-form-toggle
                      v-model:buttonValue="rowProps.row['content_filter_profile_active']"
                      :label="
                        $t('securityPolicy.configPage.pathMapping.contentFilterProfileActive')
                      "
                      data-qa="content-filter-profile-toggle"
                      :disabled="readOnly"
                    />
                  </div>
                  <div class="rbz-field">
                    <rbz-dropdown
                      :items="aclProfiles.map((item) => [item.id, item.name] as [string, string])"
                      v-model:value="rowProps.row['acl_profile']"
                      :label="$t('securityPolicy.configPage.pathMapping.aclProfile')"
                      data-qa="acl-profile-dropdown"
                      :disabled="readOnly"
                    />
                    <rbz-form-toggle
                      v-model:buttonValue="rowProps.row['acl_profile_active']"
                      :label="$t('securityPolicy.configPage.pathMapping.aclProfileActive')"
                      data-qa="acl-profile-toggle"
                      :disabled="readOnly"
                    />
                  </div>
                  <div class="rbz-field">
                    <rbz-dropdown
                      :items="
                        backendServices.map((backendService) => {
                          return [backendService.id, backendService.name] as [string, string];
                        })
                      "
                      v-model:value="rowProps.row['backend_service']"
                      :label="$t('securityPolicy.configPage.pathMapping.backendService')"
                      classes="backend-service-selection"
                      data-qa="backend-service-dropdown"
                      :disabled="readOnly"
                    />
                  </div>
                </div>
                <div class="bottom-section">
                  <div class="buttons-wrapper">
                    <rbz-form-button
                      :label="$t('securityPolicy.configPage.pathMapping.fork')"
                      variant="tertiary"
                      icon="IconFork"
                      @button-click="addNewMapEntry(rowProps.row as SecurityPolicyEntryMatch)"
                      classes="fork-entry-button"
                      data-qa="fork-entry-button"
                      :disabled="readOnly"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </rbz-table>
      </div>
      <git-history
        :api-path="gitAPIPath"
        ref="gitHistory"
        :restore-target-title="`[${entityName.plural}]`"
        @restore-version="
          loadSecurityPolicy(selectedSecurityPolicy);
          loadDropdownData();
        "
        :disabled="readOnly"
      />
      <rbz-modal-dialog
        :show="showDeleteSecurityPolicyModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteSecurityPolicy"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteSecurityPolicyModal = false"
        @closeModal="showDeleteSecurityPolicyModal = false"
        size="medium"
      >
        <template #content>
          {{
            $t('common.operations.delete.modal.message', { name: selectedSecurityPolicyData.name })
          }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>
<script lang="ts">
import _ from 'lodash';
import RequestsUtils from '@/helpers/RequestsUtils';
import {
  AclProfile,
  BackendService,
  BasicDocument,
  ContentFilterProfile,
  DocumentName,
  EdgeFunction,
  GenericObject,
  KeyOptionData,
  RateLimitRule,
  SecurityPolicy,
  SecurityPolicyEntryMatch,
} from '@/types';
import Utils from 'ui-component-library/helpers/Utils';
import { defineComponent, nextTick } from 'vue';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { useUserStore } from '@/store/UserStore';
import PageTemplate from '@/views/PageTemplate.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import RbzFormToggle from '@/components/RbzFormToggle.vue';
import RbzCustomTag from '@/components/RbzCustomTag.vue';
import TagsSelection from '@/components/TagsSelection.vue';
import KeyOption from '@/components/KeyOption.vue';
import GitHistory from '@/components/GitHistory.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import { useTagsStore } from '@/store/TagsStore';
import { usePlanetStore } from '@/store/PlanetStore';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import { useRouting } from '@/helpers/RoutingUtils';

export default defineComponent({
  name: 'SecurityPoliciesEditor',
  setup() {
    const { getEditPath, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return {
      getEditPath,
      getListPath,
      navigateTo,
      normalizeRouteName,
    };
  },
  components: {
    RbzModalDialog,
    RbzCustomTag,
    RbzFormButton,
    RbzFormToggle,
    RbzFormInput,
    RbzFormTextarea,
    GitHistory,
    KeyOption,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzTable,
    TagsSelection,
  },
  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownData: [] as BasicDocument[],
      selectedSecurityPolicy: null as string,
      selectedSecurityPolicyData: null as SecurityPolicy,
      originalSecurityPolicyData: null as SecurityPolicy,
      sessionIdsKeyOptions: [] as KeyOptionData[],
      pageLoadingCounter: 0,
      isDeleteLoading: false,
      showDeleteSecurityPolicyModal: false,

      contentFilterProfiles: [] as ContentFilterProfile[],
      aclProfiles: [] as AclProfile[],
      rateLimitRules: [] as RateLimitRule[],
      backendServices: [] as BackendService[],
      edgeFunctions: [] as EdgeFunction[],
      referencedIds: [] as string[],

      pathMappingColumns: [
        {
          id: 'name',
          title: this.$t('securityPolicy.configPage.pathMapping.name'),
          fieldNames: ['name'],
          cellContentClasses: 'ellipsis',
          isSearchable: true,
        },
        {
          id: 'match',
          title: this.$t('securityPolicy.configPage.pathMapping.matchPath'),
          fieldNames: ['match'],
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item) => {
            return item.id === '__site_level__' ? 'hidden' : '';
          },
          isSearchable: true,
        },
        {
          id: 'content-filter-profile',
          title: this.$t('securityPolicy.configPage.pathMapping.contentFilterProfile'),
          fieldNames: ['content_filter_profile'],
          displayFunction: (item) => {
            const profile = _.find(this.contentFilterProfiles, (profile) => {
              return profile.id === item['content_filter_profile'];
            });
            return profile?.name || '';
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item) => {
            return `${item['content_filter_profile_active'] ? 'rbz-text-success' : 'rbz-text-danger'}
           ${item.id === '__site_level__' ? ' hidden' : ''}`;
          },
          isSearchable: true,
        },
        {
          id: 'acl-profile',
          title: this.$t('securityPolicy.configPage.pathMapping.aclProfile'),
          fieldNames: ['acl_profile'],
          displayFunction: (item) => {
            const profile = _.find(this.aclProfiles, (profile) => {
              return profile.id === item['acl_profile'];
            });
            return profile?.name || '';
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item) => {
            return `${item['acl_profile_active'] ? 'rbz-text-success' : 'rbz-text-danger'}
           ${item.id === '__site_level__' ? ' hidden' : ''}`;
          },
          isSearchable: true,
        },
        {
          id: 'backend-service',
          title: this.$t('securityPolicy.configPage.pathMapping.backendService'),
          fieldNames: ['backend_service'],
          displayFunction: (item) => {
            return this.backendServiceName(item['backend_service']);
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
          cellContentConditionalClasses: (item) => {
            return item.id === '__site_level__' ? 'hidden' : '';
          },
          isSearchable: true,
        },
        {
          id: 'rate-limit-rules',
          title: this.$t('securityPolicy.configPage.pathMapping.rateLimitRules'),
          fieldNames: ['rate_limit_rules'],
          displayFunction: (item) => {
            return item['rate_limit_rules']?.length || 0;
          },
          classes: 'width-120px',
          cellContentClasses: 'ellipsis',
          isSearchable: true,
        },
        {
          id: 'edge-functions',
          title: this.$t('securityPolicy.configPage.pathMapping.edgeFunctions'),
          fieldNames: ['edge_functions'],
          displayFunction: (item) => {
            return item['edge_functions']?.length || 0;
          },
          classes: 'width-120px',
          cellContentClasses: 'ellipsis',
          isSearchable: true,
        },
      ] as RbzTableColumnOptions[],
      pathMappingRowButtons: [
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: (item: SecurityPolicyEntryMatch) => {
            return item.id.startsWith('__');
          },
          tooltipCallback: this.deleteEntryTooltip,
        },
      ] as RbzTableRowButton[],
      rateLimitRulesColumns: [
        {
          id: 'name',
          fieldNames: ['name'],
          title: this.$t('securityPolicy.configPage.pathMapping.rateLimitRulesTable.name'),
          displayFunction: (item) => {
            return this.rateLimitRuleDetails(item.id)?.name;
          },
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'description',
          fieldNames: ['description'],
          title: this.$t('securityPolicy.configPage.pathMapping.rateLimitRulesTable.description'),
          displayFunction: (item) => {
            return this.rateLimitRuleDetails(item.id)?.description;
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'timeframe',
          fieldNames: ['timeframe'],
          title: this.$t('securityPolicy.configPage.pathMapping.rateLimitRulesTable.timeFrame'),
          displayFunction: (item) => {
            return this.rateLimitRuleDetails(item.id)?.timeframe;
          },
          classes: 'width-100px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rateLimitRulesRowButtons: [
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      edgeFunctionsColumns: [
        {
          id: 'name',
          fieldNames: ['name'],
          title: this.$t('securityPolicy.configPage.pathMapping.edgeFunctionsTable.name'),
          displayFunction: (item) => {
            return this.edgeFunctionsDetails(item.id)?.name;
          },
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'description',
          fieldNames: ['description'],
          title: this.$t('securityPolicy.configPage.pathMapping.edgeFunctionsTable.description'),
          displayFunction: (item) => {
            return this.edgeFunctionsDetails(item.id)?.description;
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'phase',
          fieldNames: ['phase'],
          title: this.$t('securityPolicy.configPage.pathMapping.edgeFunctionsTable.phase'),
          displayFunction: (item) => {
            const phase = this.edgeFunctionsDetails(item.id)?.phase;
            return phase
              ? this.$t(
                  `edgeFunction.phaseValue.${Utils.snakeCaseToCamelCase(phase.toLowerCase())}`
                )
              : '';
          },
          classes: 'width-200px',
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      edgeFunctionsRowButtons: [
        {
          id: 'delete',
          tooltip: this.$t('common.operations.delete.tableTooltip'),
          icon: 'IconDelete',
        },
      ] as RbzTableRowButton[],
      mainSessionIdFieldActive: false,
      isNew: false,
      sessionErrorMessage: '',
    };
  },
  watch: {
    sessionIdsKeyOptions: {
      handler: function () {
        if (this.selectedSecurityPolicyData) {
          this.selectedSecurityPolicyData['session_ids'] = _.map(
            this.sessionIdsKeyOptions,
            'value'
          );
        }
      },
      deep: true,
    },
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, useTagsStore, usePlanetStore, useUserStore),

    entityName(): DocumentName {
      return {
        plural: this.$t('securityPolicy.plural'),
        singular: this.$t('securityPolicy.singular'),
      };
    },

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    gitAPIPath(): string {
      return `conf/${this.selectedBranch}/security-policies/versions`;
    },

    isLoading(): boolean {
      return (
        this.pageLoadingCounter === null || this.pageLoadingCounter > 0 || this.isDeleteLoading
      );
    },

    selectedSecurityPolicyProtected(): boolean {
      return this.selectedSecurityPolicy?.startsWith('__');
    },

    selectedSecurityPolicyReferenced(): boolean {
      return this.referencedIds.includes(this.selectedSecurityPolicy);
    },

    deleteTooltip(): string {
      if (this.selectedSecurityPolicyProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      if (this.selectedSecurityPolicyReferenced) {
        return this.$t('common.operations.delete.disabledTooltip.referenced', this.entityName);
      }
      return '';
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedSecurityPolicyData, this.originalSecurityPolicyData);
    },

    isSecurityPolicyValid(): boolean {
      const locationsValid = !_.some(this.selectedSecurityPolicyData.map, (mapEntry) => {
        return !this.isSelectedMapEntryPathValid(mapEntry.id);
      });
      const trimmedName = this.selectedSecurityPolicyData.name.toLowerCase().trim();
      const nameExists = this.dropdownData.filter((item) => {
        return item.name.toLowerCase().trim() === trimmedName;
      }).length;
      const nameValid =
        this.selectedSecurityPolicyData.name &&
        (trimmedName === this.originalSecurityPolicyData.name.toLowerCase().trim() || !nameExists);

      const sessionValid = this.hasValidSessionValues();

      return locationsValid && nameValid && sessionValid;
    },

    automaticTags(): string[] {
      const name = this.tagsStore.textToTag(this.selectedSecurityPolicyData.name);
      const nameTag = `securitypolicy:${name}`;
      return [nameTag];
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('security-policies'));
    },

    switchSecurityPolicy(securityPolicyId: string) {
      this.navigateTo(this.getEditPath('security-policies', securityPolicyId));
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.dropdownData = Utils.sortArrayByName(response?.data?.items) as BasicDocument[];
      this.setPageLoadingStatus(false);
    },

    async loadSecurityPolicy(securityPolicyId: string) {
      this.setPageLoadingStatus(true);
      this.selectedSecurityPolicy = securityPolicyId;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/security-policies/${securityPolicyId}`,
      });
      this.selectedSecurityPolicyData = response?.data || {};
      this.originalSecurityPolicyData = _.cloneDeep(this.selectedSecurityPolicyData);
      this.sessionIdsKeyOptions = _.map(
        this.selectedSecurityPolicyData['session_ids'],
        (keyValue) => {
          return {
            id: DatasetsUtils.generateUUID2(),
            value: keyValue,
          };
        }
      );
      this.setPageLoadingStatus(false);
    },

    async duplicateSecurityPolicy() {
      this.setPageLoadingStatus(true);
      const securityPolicyToAdd = _.cloneDeep(this.selectedSecurityPolicyData);
      securityPolicyToAdd.id = DatasetsUtils.generateUUID2();
      securityPolicyToAdd.name = Utils.generateUniqueEntityName(
        securityPolicyToAdd.name,
        _.map(this.dropdownData, 'name'),
        true
      );
      const successMessage = this.$t(
        'common.operations.duplicate.messages.success',
        this.entityName
      );
      const failureMessage = this.$t(
        'common.operations.duplicate.messages.failure',
        this.entityName
      );
      const url = `conf/${this.selectedBranch}/security-policies/${securityPolicyToAdd.id}`;
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'POST',
        url,
        data: securityPolicyToAdd,
        successMessage,
        failureMessage,
      });
      if (response) {
        this.switchSecurityPolicy(securityPolicyToAdd.id);
      }
      this.setPageLoadingStatus(false);
    },

    downloadSecurityPolicy() {
      Utils.downloadFile('security-policy', 'json', this.selectedSecurityPolicyData);
    },

    openDeleteSecurityPolicyModal() {
      this.showDeleteSecurityPolicyModal = true;
    },

    async deleteSecurityPolicy() {
      this.isDeleteLoading = true;
      const securityPolicyId = this.selectedSecurityPolicy;
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === securityPolicyId;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedSecurityPolicy = null;
      const successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
      const failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `conf/${this.selectedBranch}/security-policies/${securityPolicyId}`,
        successMessage,
        failureMessage,
      });
      this.redirectToList();
      this.isDeleteLoading = false;
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      let successMessage;
      let failureMessage;
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      this.selectedSecurityPolicyData.name = this.selectedSecurityPolicyData.name.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: this.isNew ? 'POST' : 'PUT',
        url: `conf/${this.selectedBranch}/security-policies/${this.selectedSecurityPolicyData.id}`,
        data: this.selectedSecurityPolicyData,
        successMessage,
        failureMessage,
        displayServerFailureMessage: true,
      });
      if (response) {
        if (this.isNew) {
          const currentRoute = this.$route.path;
          const targetRoute = currentRoute.replace(
            'new',
            `config/${this.selectedSecurityPolicyData.id}`
          );
          this.$router.push(targetRoute);
        } else {
          await this.loadDropdownData();
          this.originalSecurityPolicyData = _.cloneDeep(this.selectedSecurityPolicyData);
        }
      }
      this.setPageLoadingStatus(false);
    },

    async loadReferencedIds() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/server-groups`,
      });
      const serverGroups = response?.data?.items || [];
      const referencedSecurityPolicies: string[] = [];
      _.forEach(serverGroups, (serverGroup) => {
        referencedSecurityPolicies.push(serverGroup['security_policy']);
      });
      this.referencedIds = _.uniq(referencedSecurityPolicies);
      this.setPageLoadingStatus(false);
    },

    async loadContentFilterProfiles() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/content-filter-profiles`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.contentFilterProfiles = Utils.sortArrayByName(
        response?.data?.items
      ) as ContentFilterProfile[];
    },

    async loadAclProfiles() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/acl-profiles`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.aclProfiles = Utils.sortArrayByName(response?.data?.items) as AclProfile[];
    },

    async loadRateLimitRules() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/rate-limit-rules`,
      });
      this.rateLimitRules = Utils.sortArrayByName(response?.data?.items) as RateLimitRule[];
    },

    async loadBackendServices() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/backend-services`,
        config: { headers: { 'x-fields': 'id, name' } },
      });
      this.backendServices = Utils.sortArrayByName(response?.data?.items) as BackendService[];
    },

    async loadEdgeFunctions() {
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `conf/${this.selectedBranch}/edge-functions`,
      });
      this.edgeFunctions = Utils.sortArrayByName(response?.data?.items) as EdgeFunction[];
    },

    addSessionId() {
      this.sessionIdsKeyOptions.push({
        id: DatasetsUtils.generateUUID2(),
        value: {
          attrs: 'ip',
        },
      });
    },

    removeSessionId(index: number) {
      this.sessionIdsKeyOptions.splice(index, 1);
    },

    deleteEntryTooltip(entry: SecurityPolicyEntryMatch): string {
      if (entry.id.startsWith('__')) {
        return this.$t('common.operations.delete.disabledTooltip.protected', { singular: 'entry' });
      }
      return this.$t('common.operations.delete.tableTooltip');
    },

    addNewMapEntry(mapEntry: SecurityPolicyEntryMatch) {
      const newMapEntry = _.cloneDeep(mapEntry);
      const newMapEntryId = DatasetsUtils.generateUUID2();
      newMapEntry.id = newMapEntryId;
      newMapEntry.name = newMapEntryId;
      newMapEntry.match = `/new/path/to/match/profile/${newMapEntryId}`;

      this.selectedSecurityPolicyData.map.splice(
        this.selectedSecurityPolicyData.map.length,
        0,
        newMapEntry
      );
      const tableElement = this.$refs.pathMappingTable;
      tableElement.toggleExpander(newMapEntryId);
      tableElement.toggleExpander(mapEntry.id);
      // Wait for the expander to open
      nextTick(() => {
        const inputElement = this.$refs[`mapEntryPath${newMapEntryId}`];
        tableElement.setCurrentPageToLastPage();
        if (inputElement) {
          inputElement.focus();
        }
      });
    },

    removeMapEntry(data: GenericObject) {
      const id = data.id;
      const index = _.findIndex(this.selectedSecurityPolicyData.map, (item) => {
        return item.id === id;
      });
      this.selectedSecurityPolicyData.map.splice(index, 1);
    },

    isValidSessionValue(value: string): boolean {
      const regex = /\s/; // whitespaces

      if (value === '' || regex.test(value)) return false;
      return true;
    },

    hasValidSessionValues(): boolean {
      const sessionIds = [
        ...this.selectedSecurityPolicyData.session,
        ...this.selectedSecurityPolicyData.session_ids,
      ];
      // get values, remove default prefix jwt.
      const values = sessionIds.map((i) =>
        Object.keys(i)?.[0] === 'plugins'
          ? String(Object.values(i)?.[0]).split('jwt.')?.[1]
          : String(Object.values(i)?.[0])
      );

      // get duplicate key:value pairs
      const duplicates = sessionIds.filter((sObj, index) => {
        return index !== sessionIds.findIndex((o) => JSON.stringify(o) === JSON.stringify(sObj));
      });

      this.sessionErrorMessage = '';

      if (duplicates.length > 0) {
        this.sessionErrorMessage = 'securityPolicy.configPage.sessionInvalidDuplicate';
        return false;
      }
      for (const value of values) {
        if (!this.isValidSessionValue(value)) {
          this.sessionErrorMessage = 'securityPolicy.configPage.sessionInvalidValue';
          return false;
        }
      }

      return true;
    },

    isSelectedMapEntryPathValid(id: string): boolean {
      const index = _.findIndex(this.selectedSecurityPolicyData.map, (item) => {
        return item.id === id;
      });
      if (index === -1) {
        return false;
      }
      const matchRaw = this.selectedSecurityPolicyData.map[index].match;
      if (/\s/.test(matchRaw)) {
        return false;
      }
      const newMapEntryPath = matchRaw.trim() || '';
      const isMapEntryPathEmpty = newMapEntryPath === '';
      const isMapEntryPathDuplicate = _.some(
        this.selectedSecurityPolicyData.map,
        (item) => item.id !== id && item.match.trim() === newMapEntryPath
      );
      return !isMapEntryPathEmpty && !isMapEntryPathDuplicate;
    },

    mapEntryPathErrorMessage(id: string): string {
      const index = _.findIndex(this.selectedSecurityPolicyData.map, (item) => {
        return item.id === id;
      });
      if (index === -1) {
        return '';
      }
      const matchRaw = this.selectedSecurityPolicyData.map[index].match;
      if (/\s/.test(matchRaw)) {
        return this.$t('securityPolicy.configPage.pathMapping.matchPathWhitespace');
      }
      const path = matchRaw.trim() || '';
      const isDuplicate = _.some(
        this.selectedSecurityPolicyData.map,
        (item) => item.id !== id && item.match.trim() === path
      );
      return isDuplicate ? this.$t('securityPolicy.configPage.pathMapping.matchPathDuplicate') : '';
    },

    isMapEntryProtected(mapEntry: SecurityPolicyEntryMatch): boolean {
      return mapEntry.id.startsWith('__');
    },

    rateLimitRuleDetails(rateLimitRuleId: RateLimitRule['id']): RateLimitRule {
      return _.find(this.rateLimitRules, (rateLimitRule) => {
        return rateLimitRule.id === rateLimitRuleId;
      });
    },

    newRateLimitRule(mapEntry: SecurityPolicyEntryMatch): RateLimitRule[] {
      const siteLevelEntry = _.find(
        this.selectedSecurityPolicyData.map,
        (entry: SecurityPolicyEntryMatch) => {
          return this.isSiteLevelEntry(entry);
        }
      );
      let rateLimitIds: string[] = [];
      if (siteLevelEntry.id === mapEntry.id) {
        rateLimitIds = _.flatMap(this.selectedSecurityPolicyData.map, 'rate_limit_rules');
      } else {
        rateLimitIds = siteLevelEntry.rate_limit_rules;
      }
      return _.filter(this.rateLimitRules, (rateLimitRule) => {
        return _.indexOf(mapEntry.rate_limit_rules?.concat(rateLimitIds), rateLimitRule.id) === -1;
      });
    },

    addRateLimitRuleToEntry(
      mapEntry: SecurityPolicyEntryMatch,
      rateLimitRuleId: RateLimitRule['id']
    ) {
      if (rateLimitRuleId) {
        mapEntry['rate_limit_rules'].push(rateLimitRuleId);
      }
    },

    removeRateLimitRuleFromEntry(
      mapEntry: SecurityPolicyEntryMatch,
      rateLimitRuleId: RateLimitRule['id']
    ) {
      const index = _.findIndex(mapEntry['rate_limit_rules'], (item) => {
        return item === rateLimitRuleId;
      });
      mapEntry['rate_limit_rules'].splice(index, 1);
    },

    backendServiceName(id: string): string {
      const backendService = _.find(this.backendServices, (backendService) => {
        return backendService.id === id;
      });
      return backendService?.name || '';
    },

    edgeFunctionsDetails(edgeFunctionId: EdgeFunction['id']): EdgeFunction {
      return _.find(this.edgeFunctions, (edgeFunction) => {
        return edgeFunction.id === edgeFunctionId;
      });
    },

    newEdgeFunctions(mapEntry: SecurityPolicyEntryMatch): EdgeFunction[] {
      const siteLevelEntry = _.find(
        this.selectedSecurityPolicyData.map,
        (entry: SecurityPolicyEntryMatch) => {
          return this.isSiteLevelEntry(entry);
        }
      );
      let edgeFunctionIds: string[] = [];
      if (siteLevelEntry.id === mapEntry.id) {
        edgeFunctionIds = _.flatMap(this.selectedSecurityPolicyData.map, 'edge_functions');
      } else {
        edgeFunctionIds = siteLevelEntry.edge_functions;
      }
      return _.filter(this.edgeFunctions, (edgeFunction) => {
        return _.indexOf(mapEntry?.edge_functions?.concat(edgeFunctionIds), edgeFunction.id) === -1;
      });
    },

    addEdgeFunctionToEntry(mapEntry: SecurityPolicyEntryMatch, edgeFunctionId: string) {
      if (edgeFunctionId) {
        mapEntry['edge_functions'].push(edgeFunctionId);
      }
    },

    removeEdgeFunctionFromEntry(mapEntry: SecurityPolicyEntryMatch, edgeFunctionId: string) {
      const index = _.findIndex(mapEntry['edge_functions'], (item) => {
        return item === edgeFunctionId;
      });
      mapEntry['edge_functions'].splice(index, 1);
    },

    newSecurityPolicy(): SecurityPolicy {
      const factory = DatasetsUtils.newEntityFactory['security-policies'];
      return factory && factory();
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedSecurityPolicyData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData as SecurityPolicy, Boolean(entityNotSaved));
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },

    isSiteLevelEntry(entry: SecurityPolicyEntryMatch): boolean {
      return entry.id === '__site_level__';
    },
  },
  created() {
    const tagsStore = useTagsStore();
    this.$watch(
      () => [tagsStore.editorDocId, tagsStore.editorDocType],
      async ([docId, docType]) => {
        if (!docId || docType !== 'security-policies') {
          return;
        }
        const backendServicesPromise = this.loadBackendServices();
        const contentFilterProfilesPromise = this.loadContentFilterProfiles();
        const aclProfilesPromise = this.loadAclProfiles();
        const rateLimitRulesPromise = this.loadRateLimitRules();
        const edgeFunctionsPromise = this.loadEdgeFunctions();
        await Promise.all([
          backendServicesPromise,
          contentFilterProfilesPromise,
          aclProfilesPromise,
          rateLimitRulesPromise,
          edgeFunctionsPromise,
        ]);
        await this.branchesStore.getBranches();
        await this.loadSecurityPolicy(docId);
      },
      { immediate: true }
    );
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['doc_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['New Security Policy', 'Security Policies Editor'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedSecurityPolicyData = this.newSecurityPolicy();
            this.selectedSecurityPolicyData.name = Utils.generateUniqueEntityName(
              this.selectedSecurityPolicyData.name,
              _.map(this.dropdownData, 'name')
            );
            this.originalSecurityPolicyData = _.cloneDeep(this.selectedSecurityPolicyData);
          } else {
            const securityPolicyId = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === securityPolicyId;
            });
            await this.loadReferencedIds();
            if (dataExists) {
              await this.loadSecurityPolicy(securityPolicyId);
            } else {
              this.redirectToList();
              return;
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedSecurityPolicyData = JSON.parse(savedSessionData);
            this.sessionIdsKeyOptions = _.map(
              this.selectedSecurityPolicyData['session_ids'],
              (keyValue) => {
                return {
                  id: DatasetsUtils.generateUUID2(),
                  value: keyValue,
                };
              }
            );
          }
          const backendServicesPromise = this.loadBackendServices();
          const contentFilterProfilesPromise = this.loadContentFilterProfiles();
          const aclProfilesPromise = this.loadAclProfiles();
          const rateLimitRulesPromise = this.loadRateLimitRules();
          const edgeFunctionsPromise = this.loadEdgeFunctions();
          await Promise.all([
            backendServicesPromise,
            contentFilterProfilesPromise,
            aclProfilesPromise,
            rateLimitRulesPromise,
            edgeFunctionsPromise,
          ]);
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

:deep(.rbz-table .has-errors) {
  .data-cell {
    color: map.get(colors.$colors, 'cinnabar');
  }
}

:deep(.input-icon) {
  top: 31px;
}

.right-side-column {
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .top-section {
    display: flex;
    flex-direction: column;
    justify-content: start;
    gap: 40px;
  }
}

.buttons-wrapper {
  display: flex;
  justify-content: flex-end;
}

:deep(.path-mapping-table) {
  .switch-button-holder {
    margin-top: 8px;
  }
}

.rate-limit-rules-table,
.edge-functions-table {
  :deep(.rbz-table th) {
    border-bottom: 1px solid map.get(colors.$colors, 'silver');
  }
}

:deep(.rbz-table) {
  .data-cell .hidden {
    display: none;
  }
}
</style>
